import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useEventStore,
  useGuestStore,
  useTimeToReleaseUploadStore,
} from "store";
import * as SpacesService from "libs/digitalocean/SpacesService";
import bingoBlackIcon from "../../assets/images/bingo-black-icon.png";
import bingoRedIcon from "../../assets/images/bingo-red-icon.png";
import recorderBlackIcon from "../../assets/images/recorder-black-icon.png";
import recorderRedIcon from "../../assets/images/recorder-red-icon.png";
import uploaderBlackIcon from "../../assets/images/uploader-black-icon.png";
import uploaderRedIcon from "../../assets/images/uploader-red-icon.png";
import logoBlackIcon from "../../assets/images/logo-black-icon.png";
import galleryBlackIcon from "../../assets/images/gallery-black-icon.png";
import galleryRedIcon from "../../assets/images/gallery-red-icon.png";
import rankingBlackIcon from "../../assets/images/ranking-black-icon.svg";
import rankingRedIcon from "../../assets/images/ranking-red-icon.svg";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { Edit, User } from "assets/svg";
import { clarity } from "react-microsoft-clarity";
import TimeToShareModal from "components/TimeToShareModal";
import GamesModal from "./GamesModal";
import RankingModal from "./RankingModal";

const BLACK_ICONS = {
  gallery: galleryBlackIcon,
  bingo: bingoBlackIcon,
  recorder: recorderBlackIcon,
  uploader: uploaderBlackIcon,
  logo: logoBlackIcon,
  ranking: rankingBlackIcon,
};

const RED_ICONS = {
  gallery: galleryRedIcon,
  bingo: bingoRedIcon,
  recorder: recorderRedIcon,
  uploader: uploaderRedIcon,
  ranking: rankingRedIcon,
};

interface OptionCardToFeedHeaderProps {
  icon: string;
  title: string;
  onClick: () => void;
}
export const OptionCardToFeedHeader = ({
  icon,
  title,
  onClick,
}: OptionCardToFeedHeaderProps) => {
  const { theme } = useEventStore();
  return (
    <button
      type="button"
      className={`w-full flex items-center px-5 py-2 gap-2 card ${theme.card} transition-transform duration-200 shadow-md hover:shadow-lg`}
      onClick={onClick}
    >
      <img
        src={icon}
        alt={title}
        className="w-8 transition-transform max-h-20 object-contain"
      />
      <div className="flex flex-col items-center gap-1 grow">
        <h3 className="text-xs font-semibold text-theme-black text-center">
          {title}
        </h3>
      </div>
    </button>
  );
};

interface FeedHeaderProps {
  setCreatePost: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function FeedHeader({ setCreatePost }: FeedHeaderProps) {
  const navigate = useNavigate();
  const intl = useIntl();
  const { event, theme } = useEventStore();
  const { guest } = useGuestStore();
  const [guestPhotoUrl, setGuestPhotoUrl] = useState<string | null>(null);
  const [chooseTimeToShareModalOpen, setChooseTimeToShareModalOpen] =
    useState(false);
  const { setTimeToReleaseChoice } = useTimeToReleaseUploadStore();
  const [gamesModalOpen, setGamesModalOpen] = useState(false);
  const [rankingModalOpen, setRankingModalOpen] = useState(false);

  const headerIcons = useMemo(
    () => (theme.name === "rose" ? RED_ICONS : BLACK_ICONS),
    [theme]
  );

  const bingoGameEnabled = useMemo(
    () => event?.betaFeatures?.includes("bingo"),
    [event]
  );

  const photoChallengeGameEnabled = useMemo(
    () => event?.betaFeatures?.includes("photo_challenge"),
    [event]
  );

  const quizGameEnabled = useMemo(
    () => event?.betaFeatures?.includes("quiz"),
    [event]
  );

  const teamMatchEnabled = useMemo(
    () => event?.betaFeatures?.includes("team_match"),
    [event]
  );

  const hasAnyGameEnabled = useMemo(
    () =>
      bingoGameEnabled ||
      photoChallengeGameEnabled ||
      quizGameEnabled ||
      teamMatchEnabled,
    [
      bingoGameEnabled,
      photoChallengeGameEnabled,
      quizGameEnabled,
      teamMatchEnabled,
    ]
  );

  useEffect(() => {
    const fetchGuestPhoto = async () => {
      const url = await SpacesService.getFileUrl(
        guest?.photoStoragePath as string
      );
      setGuestPhotoUrl(url);
    };

    if (guest?.photoStoragePath) {
      fetchGuestPhoto();
    }
  }, [guest]);

  const handleRecordClick = useCallback(() => {
    if (event?.uploadsReleaseMode === "mixedRelease") {
      setChooseTimeToShareModalOpen(true);
      console.log(chooseTimeToShareModalOpen, setChooseTimeToShareModalOpen);
    } else {
      if (event?.uploadsReleaseMode === "realtimeRelease") {
        setTimeToReleaseChoice("release-now");
      }
      if (event?.uploadsReleaseMode === "oneYearCapsuleRelease") {
        setTimeToReleaseChoice("release-later");
      }
      navigate("/record/form");
    }
  }, [
    event,
    navigate,
    setChooseTimeToShareModalOpen,
    setTimeToReleaseChoice,
    chooseTimeToShareModalOpen,
  ]);

  const openUrlInNewTab = (url: string) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      setTimeout(() => {
        link.click();
        document.body.removeChild(link);
      }, 100);
    } else {
      window.open(url, "_blank");
    }
  };

  const handleGalleryClick = useCallback(() => {
    const eventGalleryLink = `https://app.capsuladotempo.com/public/${event?.code}/gallery`;
    openUrlInNewTab(eventGalleryLink);
  }, [event]);

  const handleRankingClick = useCallback(() => {
    setRankingModalOpen(true);
    if (clarity.hasStarted()) {
      clarity.setTag("feed_ranking", "viewed");
    }
  }, []);

  const handleGamesClick = useCallback(() => {
    setGamesModalOpen(true);
    if (clarity.hasStarted()) {
      clarity.setTag("feed_games_menu", "opened");
    }
  }, []);

  return (
    <>
      <div
        className={`grid ${
          !hasAnyGameEnabled && !event?.publicGallery
            ? "grid-cols-2"
            : "grid-cols-3"
        } gap-1 w-full items-center justify-between `}
      >
        <OptionCardToFeedHeader
          icon={headerIcons.uploader}
          title={intl.formatMessage({
            id: "feed.menu.button.options.upload",
            defaultMessage: "Escolher Arquivos",
          })}
          onClick={() => {
            navigate("/upload");
          }}
        />

        <OptionCardToFeedHeader
          icon={headerIcons.recorder}
          title={intl.formatMessage({
            id: "feed.menu.button.options.record",
            defaultMessage: "Gravar Agora",
          })}
          onClick={handleRecordClick}
        />
        {hasAnyGameEnabled && (
          <OptionCardToFeedHeader
            icon={headerIcons.bingo}
            title={intl.formatMessage({
              id: "feed.menu.button.options.games",
              defaultMessage: "Games",
            })}
            onClick={handleGamesClick}
          />
        )}
        {!hasAnyGameEnabled && event?.publicGallery && (
          <OptionCardToFeedHeader
            icon={headerIcons.gallery}
            title={intl.formatMessage({
              id: "feed.menu.button.options.gallery",
              defaultMessage: "Gallery",
            })}
            onClick={handleGalleryClick}
          />
        )}
      </div>
      <div className="relative w-full inline-flex items-center gap-1">
        <button
          className="w-full p-0"
          onClick={() => {
            setCreatePost(true);
          }}
        >
          <div
            className={`${theme.card} relative card w-full mx-auto m-1 mb-4 flex items-center gap-2 p-2`}
          >
            <div className="w-14 h-14 grow-0 shrink-0 rounded-full overflow-hidden border-2 border-gray-200">
              {guestPhotoUrl ? (
                <img
                  src={guestPhotoUrl}
                  alt={guest?.name || "Guest"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User width={24} height={24} color="none" outline="#414141" />
                </div>
              )}
            </div>

            <input
              type="text"
              disabled={false}
              readOnly
              placeholder={intl.formatMessage({
                id: "feed.placeholder.newPost",
                defaultMessage: "Criar post...",
              })}
              className={`flex-1 px-4 py-3 ${theme.input} flex items-center justify-between`}
            />
            <div className="absolute right-2 -translate-x-full">
              <Edit width={24} height={24} color="none" outline="#414141" />
            </div>
          </div>
        </button>
        <button
          onClick={handleRankingClick}
          className={`${theme.card} card py-1 px-1 mt-1 mb-4`}
        >
          <img src={headerIcons.ranking} alt="Ranking" className="h-16 w-10" />
        </button>
      </div>
      <TimeToShareModal
        setChooseTimeToShareModalOpen={setChooseTimeToShareModalOpen}
        chooseTimeToShareModalOpen={chooseTimeToShareModalOpen}
      />
      <GamesModal
        isOpen={gamesModalOpen}
        onClose={() => setGamesModalOpen(false)}
      />
      <RankingModal
        isOpen={rankingModalOpen}
        onClose={() => setRankingModalOpen(false)}
      />
    </>
  );
}
